import { createTheme, darken, lighten, ThemeOptions } from '@mui/material';
import type { PaletteColor } from '@mui/material/styles';
import { ButtonTheme } from './components/ButtonTheme';
import { ChipTheme } from './components/ChipTheme';
import { TooltipTheme } from './components/TooltipTheme';

export const getTheme = (brandColor: string = '#4B22A8'): ThemeOptions =>
  createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: brandColor,
        light: lighten(brandColor, 0.9),
        dark: darken(brandColor, 0.2)
      },
      secondary: {
        main: '#777777',
        light: '#fafafa'
      },
      border: {
        main: '#ececec'
      },
      common: {
        black: '#09090a',
        white: '#ffffff'
      },
      background: {
        default: '#f8f9fa',
        paper: '#fff'
      },
      error: {
        main: '#da0808',
        light: '#fff2f2'
      },
      warning: {
        main: '#d97706',
        light: '#fffbeb'
      },
      success: {
        main: '#077f13',
        light: '#dbfcde'
      },
      purple: {
        main: '#7c00b1',
        light: '#f5dfff',
        dark: darken('#7c00b1', 0.2),
        contrastText: '#FFF2DF'
      },
      orange: {
        main: '#fb8703',
        light: '#fff3e5',
        dark: darken('#fb8703', 0.2),
        contrastText: '#FFF2DF'
      },
      blue: {
        main: '#1e40af',
        light: '#eef5ff',
        dark: darken('#1e40af', 0.2),
        contrastText: '#FFF2DF'
      },
      text: {
        primary: '#242424',
        secondary: '#888888',
        disabled: '#777777'
      },
      info: {
        main: '#ECECEC'
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: 'Inter', 'sans-serif';
            font-display: swap;
          }
        `
      },
      MuiButton: ButtonTheme(),
      MuiTooltip: TooltipTheme(),
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            fontSize: '0.75rem',
            '@media (max-width:899.95px)': {
              fontSize: '12px'
            }
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              backgroundColor: '#f0f0f0'
            },
            fontSize: '16px',
            '@media (max-width:899.95px)': {
              fontSize: '14px'
            }
          },
          input: {
            fontSize: '16px',
            '@media (max-width:899.95px)': {
              fontSize: '14px'
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '16px',
            '@media (max-width:899.95px)': {
              fontSize: '14px'
            }
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            color: '#DA0808',
            '&$error': {
              color: '#DA0808'
            }
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: {
            tableLayout: 'fixed'
          }
        }
      },
      MuiChip: ChipTheme(),
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: '40px'
          },
          indicator: {
            display: 'none'
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            textTransform: 'none',
            minHeight: '40px',
            padding: '8px 16px',
            '&.Mui-selected': {
              backgroundColor: lighten(brandColor, 0.9),
              color: '#242424'
            }
          }
        }
      }
    },
    typography: {
      fontFamily: ['Inter', 'sans-serif'].join(','),
      body1: {
        fontSize: '1rem',
        lineHeight: '1.5rem',
        '@media (max-width:899.95px)': {
          fontSize: '0.875rem',
          lineHeight: '1.25rem'
        }
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        '@media (max-width:899.95px)': {
          fontSize: '0.75rem',
          lineHeight: '1.125rem'
        }
      },
      h1: {
        fontSize: '1.5rem',
        lineHeight: '2rem',
        '@media (max-width:899.95px)': {
          fontSize: '1.25rem',
          lineHeight: '1.75rem'
        }
      },
      h2: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        '@media (max-width:899.95px)': {
          fontSize: '1.125rem',
          lineHeight: '1.5rem'
        }
      },
      h3: {
        fontSize: '0.875rem',
        lineHeight: '1.25rem'
      },
      h6: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
        '@media (max-width:899.95px)': {
          fontSize: '1.125rem',
          lineHeight: '1.5rem'
        }
      }
    }
  });

declare module '@mui/material/styles' {
  interface Palette {
    border: { main: string };
    orange: PaletteColor;
    purple: PaletteColor;
    blue: PaletteColor;
  }
  interface PaletteOptions {
    border?: { main: string };
    orange?: PaletteColor;
    purple?: PaletteColor;
    blue?: PaletteColor;
  }
}
